import { create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  getAdvertisements,
  createAdvertisement as apiCreateAdvertisement,
  updateAdvertisement as apiUpdateAdvertisement,
  toggleAdvertisementStatus as apiToggleAdvertisementStatus,
  deleteAdvertisement as apiDeleteAdvertisement,
  reorderAdvertisements as apiReorderAdvertisements,
} from "../setup/api/services/advertisements";
import {
  IAdvertisement,
  EAdvertisementStatus,
  ICreateAdvertisementBody,
  IUpdateAdvertisementBody,
} from "../setup/api/services/advertisements/models";

interface FetchParams {
  page?: number;
  size?: number;
}

interface AdvertisementsState {
  advertisements: IAdvertisement[];
  loading: boolean;
  error: string | null;
  fetchAdvertisements: (params?: FetchParams) => Promise<void>;
  createAdvertisement: (
    advertisementData: ICreateAdvertisementBody
  ) => Promise<IAdvertisement | null>;
  updateAdvertisement: (
    id: string,
    advertisementData: IUpdateAdvertisementBody
  ) => Promise<IAdvertisement | null>;
  toggleAdvertisementStatus: (id: string) => Promise<IAdvertisement | void>;
  deleteAdvertisement: (id: string) => Promise<void>;
  reorderAdvertisements: (orderedIds: string[]) => Promise<void>;
  setAdvertisements: (advertisements: IAdvertisement[]) => void;
}

export const useAdvertisementsStore = create<AdvertisementsState>()(
  devtools((set, get) => ({
    advertisements: [],
    loading: false,
    error: null,

    setAdvertisements: (advertisements) => set({ advertisements }),

    createAdvertisement: async (
      advertisementData: ICreateAdvertisementBody
    ) => {
      try {
        set({ loading: true, error: null });
        const newAdvertisement = await apiCreateAdvertisement(
          advertisementData
        );
        set({ advertisements: [newAdvertisement, ...get().advertisements] });
        set({ loading: false });
        return newAdvertisement;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error creating advertisement:", err);
        set({ error: "Failed to create advertisement" });
        set({ loading: false });
        throw err;
      }
    },

    updateAdvertisement: async (
      id: string,
      advertisementData: IUpdateAdvertisementBody
    ) => {
      try {
        set({ loading: true, error: null });
        const updatedAdvertisement = await apiUpdateAdvertisement(
          id,
          advertisementData
        );
        set({
          advertisements: get().advertisements.map((advertisement) =>
            advertisement.id === id ? updatedAdvertisement : advertisement
          ),
        });
        return updatedAdvertisement;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error updating advertisement:", err);
        set({ error: "Failed to update advertisement" });
        return null;
      } finally {
        set({ loading: false });
      }
    },

    fetchAdvertisements: async (params = { page: 0, size: 100 }) => {
      try {
        set({ loading: true, error: null });
        const response = await getAdvertisements(params);
        set({ advertisements: response.data.promotionDtos || [] });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error fetching advertisements:", err);
        set({ error: "Failed to fetch advertisements" });
      } finally {
        set({ loading: false });
      }
    },

    toggleAdvertisementStatus: async (id: string) => {
      const { advertisements } = get();
      const target = advertisements.find((a) => a.id === id);
      if (!target) return;

      const newStatus =
        target.status === EAdvertisementStatus.ACTIVE
          ? EAdvertisementStatus.INACTIVE
          : EAdvertisementStatus.ACTIVE;

      try {
        const updatedAdvertisement = await apiToggleAdvertisementStatus(
          id,
          newStatus
        );
        set({
          advertisements: advertisements.map((a) =>
            a.id === id ? updatedAdvertisement : a
          ),
        });
        return updatedAdvertisement;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error toggling advertisement status:", err);
        set({ error: "Failed to update advertisement status" });
        throw err;
      }
    },

    deleteAdvertisement: async (id: string) => {
      try {
        await apiDeleteAdvertisement(id);
        set({
          advertisements: get().advertisements.filter((a) => a.id !== id),
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error deleting advertisement:", err);
        set({ error: "Failed to delete advertisement" });
      }
    },

    reorderAdvertisements: async (orderedIds: string[]) => {
      const previousOrder = get().advertisements;
      try {
        // Optimistically update the order
        const reorderedAds = orderedIds
          .map((id) => previousOrder.find((ad) => ad.id === id))
          .filter((ad): ad is IAdvertisement => ad !== undefined);

        set({ advertisements: reorderedAds });

        // Call the API
        await apiReorderAdvertisements(orderedIds);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error reordering advertisements:", err);
        // Revert to previous order on error
        set({
          advertisements: previousOrder,
          error: "Failed to reorder advertisements",
        });
        throw err;
      }
    },
  }))
);
